/**
 * O seletor de arquivo escondido que os botões de importação abrem.
 *
 * Papel: reproduz os `<input type="file" style="display:none">` da origem
 * (l. 497–498, 710): o botão visível chama `click()` nele, e a escolha chega a
 * quem monta por `onArquivo`.
 *
 * Como usar: quem monta guarda a referência e a passa em `ref`. O valor do
 * campo é limpo a cada escolha, como na origem, para que escolher o mesmo
 * arquivo de novo — depois de corrigir a planilha, por exemplo — leia de novo;
 * sem isso o navegador não dispara a troca.
 */
import { type ChangeEvent, type Ref } from 'react';

interface EntradaDeArquivoProps {
  /** O `accept` do campo: `application/pdf`, `.xlsx,.xlsm`. */
  aceita: string;
  /** O nome acessível do campo, que diz de que importação ele é. */
  rotulo: string;
  onArquivo: (arquivo: File) => void;
  ref: Ref<HTMLInputElement>;
}

/**
 * O campo de arquivo escondido.
 *
 * @param aceita - Os tipos aceitos.
 * @param rotulo - O nome acessível.
 * @param onArquivo - Chamado com o arquivo escolhido.
 * @param ref - A referência que o botão usa para abrir a escolha.
 * @returns O `<input>` escondido.
 */
export function EntradaDeArquivo({ aceita, rotulo, onArquivo, ref }: EntradaDeArquivoProps) {
  /**
   * Entrega o arquivo escolhido e limpa o campo.
   *
   * @param evento - A troca do campo.
   */
  function handleTroca(evento: ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.target.files?.[0];
    evento.target.value = '';
    if (arquivo !== undefined) onArquivo(arquivo);
  }

  return (
    <input
      ref={ref}
      type="file"
      accept={aceita}
      aria-label={rotulo}
      tabIndex={-1}
      className="hidden"
      onChange={handleTroca}
    />
  );
}
